import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { LoggerService } from '@app/core/infrastructure';
import { Avatar, ApiErrorResponse, Paginated, AvatarUpdateRequest, DeleteAllAvatarsResponse } from './avatar-api.models';
import { Gender } from '@app/shared/models';

/**
 * Pure API service for avatar-related HTTP operations
 * No state management - just HTTP calls
 */
@Injectable({
  providedIn: 'root'
})
export class AvatarApiService {
  private readonly logger = inject(LoggerService).scope('AvatarApiService');
  
  private apiBaseUrl = `${environment.API_BASE_URL}/protected/avatars`;

  constructor(
    private http: HttpClient
  ) {}

  /**
   * POST /create - Create a new avatar from image
   */
  createAvatar(image: File, faceName?: string, gender?: Gender): Observable<Avatar | ApiErrorResponse> {
    const formData = new FormData();
    formData.append('image', image);

    let params = new HttpParams();
    if (faceName) {
      params = params.set('face_name', faceName);
    }
    if (gender) {
      params = params.set('gender', gender);
    }

    return this.http.post<Avatar | ApiErrorResponse>(`${this.apiBaseUrl}/create`, formData, { params }).pipe(
      catchError(error => {
        this.logger.error('Error creating avatar:', error);
        // Re-throw the error to preserve the message from the interceptor
        return throwError(() => error);
      })
    );
  }

  /**
   * GET /status/:characterUid - Get avatar creation status
   */
  getAvatarStatus(characterUid: string): Observable<Avatar | ApiErrorResponse> {
    return this.http.get<Avatar | ApiErrorResponse>(`${this.apiBaseUrl}/status/${characterUid}`).pipe(
      catchError(error => {
        this.logger.error('Error getting avatar status:', error);
        return throwError(() => new Error('Failed to get avatar status'));
      })
    );
  }

  /**
   * GET /list - List user avatars with pagination
   */
  listUserAvatars(page: number = 1, size: number = 10): Observable<Paginated<Avatar> | ApiErrorResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    
    return this.http.get<Paginated<Avatar> | ApiErrorResponse>(`${this.apiBaseUrl}/list`, { params }).pipe(
      retry({
        count: 3,
        delay: (error, retryCount) => {
          // Only retry on 404 errors (user not found)
          if (error.status === 404) {
            this.logger.debug(`Retrying listUserAvatars() attempt ${retryCount} due to 404 error`);
            // Exponential backoff: 1s, 2s, 4s
            return timer(Math.pow(2, retryCount - 1) * 1000);
          }
          // For other errors, don't retry
          throw error;
        }
      }),
      catchError(error => {
        this.logger.error('Error listing avatars:', error);
        return throwError(() => new Error('Failed to list avatars'));
      })
    );
  }

  /**
   * DELETE /:id - Delete an avatar
   */
  deleteAvatar(id: string): Observable<void | ApiErrorResponse> {
    return this.http.delete<void | ApiErrorResponse>(`${this.apiBaseUrl}/${id}`).pipe(
      catchError(error => {
        this.logger.error('Error deleting avatar:', error);
        return throwError(() => new Error('Failed to delete avatar'));
      })
    );
  }

  /**
   * PATCH /:id - Update avatar
   */
  updateAvatar(id: string, updates: AvatarUpdateRequest): Observable<Avatar | ApiErrorResponse> {
    return this.http.patch<Avatar | ApiErrorResponse>(`${this.apiBaseUrl}/${id}`, updates).pipe(
      catchError(error => {
        this.logger.error('Error updating avatar:', error);
        return throwError(() => new Error('Failed to update avatar'));
      })
    );
  }

  /**
   * DELETE /all - Delete all user avatars
   */
  deleteAllAvatars(): Observable<DeleteAllAvatarsResponse> {
    return this.http.delete<DeleteAllAvatarsResponse>(`${this.apiBaseUrl}/all`).pipe(
      catchError(error => {
        this.logger.error('Error deleting all avatars:', error);
        return throwError(() => new Error('Failed to delete all avatars'));
      })
    );
  }
}
